import React from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import ReplayIcon from "@mui/icons-material/Replay";
import DataObjectIcon from "@mui/icons-material/DataObject";

import PaperFragment from "../../../../helper-components/misc/PaperFragment";
import StackedHeader from "../../../../helper-components/misc/StackedHeader";
import DataGridTable from "../../../../helper-components/table/DataGridTable";

import ResourceEditForm from "./ResourceEditForm";
import ResourceAddStepper from "./ResourceAddStepper";
import ResourceMultiEditForm from "./ResourceMultiEditForm";

import { createUploadObject } from "../../../../helper-functions/shared/miscUtils";
import { columns, defaultTableOptions } from "../../../../helper-functions/table/resources";
import { createCookieStepper } from "../../../../helper-functions/api/cookieQueries";
import {
    approveResource,
    approveResources,
    createResource,
    deleteResources,
    queryResources,
    retryResource,
    retryResourceMetadata,
    retryResourceMetadatas,
    retryResources,
    updateResource,
    updateResources
} from "../../../../helper-functions/api/resourceQueries";

import { getLanguage } from "../../../../config/language/language";

/**
 * Table showing resources.
 * If no data found, show error message instead.
 */
export default function ResourcesTable(props) {
    const resources = props.backend.resources;

    const showCreateDialog = () => {
        props.showDialog({
            title: "Add new resource",
            component: <ResourceAddStepper {...props} callback={(confirm, options) => handleStepper(confirm, options)} />
        });
    };

    const showDeleteDialog = () => {
        props.showDialog({
            title: "Delete resource(s)",
            message: `Are you sure you want to delete the ${resources.selected.length} selected resource(s)?

            All data, including transcripts and quotes, will be lost.`,
            actionTitle: "Delete",
            actionColor: "error",
            callback: (confirm) => handleDelete(confirm)
        });
    };

    const showUpdateDialog = () => {
        if (resources.selected.length === 1) {
            props.showDialog({
                title: "Edit resource",
                message: "Click 'Update' to confirm changes.",
                component: <ResourceEditForm {...props} row={resources.selected?.[0]} callback={(confirm, options) => handleUpdate(confirm, options)} />
            });
        } else if (resources.selected.length > 1) {
            props.showDialog({
                title: "Edit resources",
                message: "Click 'Update' to confirm changes.",
                component: <ResourceMultiEditForm {...props} row={resources.selected?.[0]} callback={(confirm, options) => handleMultiUpdate(confirm, options)} />
            });
        }
    };

    const showApproveDialog = () => {
        const handle = resources.selected.length > 1 ? handleMultiApprove : handleApprove;

        props.showDialog({
            title: "Approve resource(s)",
            message: `Are you sure you want to approve the ${resources.selected.length} selected resource(s)?
            After approval, each resource will shortly begin further processing by creating a transcript.`,
            actionTitle: "Approve",
            actionColor: "success",
            callback: (confirm) => handle(confirm)
        });
    };

    const showRetryDialog = () => {
        const handle = resources.selected.length > 1 ? handleMultiRetry : handleRetry;

        props.showDialog({
            title: "Retry resource(s)",
            message: `Are you sure you want to retry the processing of the ${resources.selected.length} selected resource(s)?
            This will delete each transcript and start the processing from scratch.`,
            actionTitle: "Retry",
            actionColor: "success",
            callback: (confirm) => handle(confirm)
        });
    };

    const showRetryMetadataDialog = () => {
        const handle = resources.selected.length > 1 ? handleMultiRetryMetadata : handleRetryMetadata;

        props.showDialog({
            title: "Retry resource(s) metadata",
            message: `Are you sure you want to retry the metadata processing of the ${resources.selected.length} selected resource(s)?
            This will try to update each resource by querying the title, description, thumbnail, etc.`,
            actionTitle: "Retry metadata",
            actionColor: "success",
            callback: (confirm) => handle(confirm)
        });
    };

    const handleStepper = (confirm, options = {}) => {
        props.setOpenDialog(false); // Manually close the dialog.
        if (!confirm) return;

        const { row, cookieRow } = options;

        // Create cookie before sending the resource request.
        if (cookieRow?.type && cookieRow?.text) {
            const body = createUploadObject(cookieRow, ["form"]);
            createCookieStepper(props, { body }, (response) => {
                handleCreate({ row });
            });
        } else {
            handleCreate({ row });
        }
    };

    const handleCreate = (options = {}) => {
        const { row } = options;

        const body = createUploadObject(row, ["form", "_*"]);

        props.showSnackbar({ color: "info", message: getLanguage().SNACKBAR_INFO_LOADING, duration: 10000 });
        createResource(props, { body });
    };

    const handleDelete = (confirm) => {
        if (!confirm) return;

        const ids = resources.selected.map((resource) => resource.id);
        deleteResources(props, { body: { ids }, ids });
    };

    const handleUpdate = (confirm, options = {}) => {
        props.setOpenDialog(false); // Manually close the dialog.
        if (!confirm) return;

        const { row } = options;
        const body = createUploadObject(row, ["form", "_*"]);

        updateResource(props, { body, id: row.id });
    };

    const handleMultiUpdate = (confirm, options = {}) => {
        props.setOpenDialog(false); // Manually close the dialog.
        if (!confirm) return;

        const { row } = options;
        const body = createUploadObject(row, ["form", "_*"]);

        const ids = resources.selected.map((resource) => resource.id);
        body.ids = ids;

        updateResources(props, { body });
    };

    const handleApprove = (confirm) => {
        if (!confirm) return;

        const ids = resources.selected.map((resource) => resource.id);
        approveResource(props, { id: ids?.[0] });
    };

    const handleMultiApprove = (confirm) => {
        if (!confirm) return;

        const body = {};
        const ids = resources.selected.map((resource) => resource.id);
        body.ids = ids;

        approveResources(props, { body });
    };

    const handleRetry = (confirm) => {
        if (!confirm) return;

        const ids = resources.selected.map((resource) => resource.id);
        retryResource(props, { id: ids?.[0] });
    };

    const handleMultiRetry = (confirm) => {
        if (!confirm) return;

        const body = {};
        const ids = resources.selected.map((resource) => resource.id);
        body.ids = ids;

        retryResources(props, { body, ids });
    };

    const handleRetryMetadata = (confirm) => {
        if (!confirm) return;

        const ids = resources.selected.map((resource) => resource.id);

        props.showSnackbar({ color: "info", message: getLanguage().SNACKBAR_INFO_LOADING, duration: 10000 });
        retryResourceMetadata(props, { id: ids?.[0] });
    };

    const handleMultiRetryMetadata = (confirm) => {
        if (!confirm) return;

        const body = {};
        const ids = resources.selected.map((resource) => resource.id);
        body.ids = ids;

        props.showSnackbar({ color: "info", message: getLanguage().SNACKBAR_INFO_LOADING, duration: 10000 });
        retryResourceMetadatas(props, { body });
    };

    return (
        <PaperFragment
            className="tour-resources-3 tour-resources-4 tour-resources-5 tour-resources-6"
            title={
                <StackedHeader
                    {...props}
                    title="Resources"
                    subtitle="View and manage resources."
                    icons={[
                        {
                            icon: <DeleteIcon />,
                            tooltip: "Delete selected resources",
                            disabled: resources.selected.length === 0 || resources.selected.some((r) => ["IN_PROGRESS"].includes(r.status)),
                            onClick: showDeleteDialog
                        },
                        {
                            icon: <EditIcon />,
                            tooltip: "Edit selected resource",
                            onClick: showUpdateDialog,
                            disabled: resources.selected.length === 0
                        },
                        {
                            icon: <CheckIcon />,
                            tooltip: "Approve selected resource",
                            onClick: showApproveDialog,
                            disabled: resources.selected.length === 0 || !resources.selected.every((r) => r.status === "AWAITING_APPROVAL")
                        },
                        {
                            icon: <ReplayIcon />,
                            tooltip: "Retry selected resource",
                            onClick: showRetryDialog,
                            disabled: resources.selected.length === 0 || resources.selected.some((r) => ["IN_QUEUE", "IN_PROGRESS"].includes(r.status))
                        },
                        {
                            icon: <DataObjectIcon />,
                            tooltip: "Retry querying selected resource metadata",
                            onClick: showRetryMetadataDialog,
                            disabled: resources.selected.length === 0 || resources.selected.some((r) => ["IN_QUEUE", "IN_PROGRESS"].includes(r.status))
                        }
                    ]}
                    buttons={[
                        {
                            icon: <AddIcon />,
                            title: "New resource",
                            onClick: showCreateDialog
                        }
                    ]}
                />
            }
            value={
                <DataGridTable
                    {...props}
                    {...defaultTableOptions}
                    rowHeight={60}
                    states={resources}
                    columns={columns(props)}
                    query={(options) => queryResources(props, options)}
                    empty={getLanguage().NO_RESOURCES_FOUND}
                    refresh={10}
                />
            }
        />
    );
}
